import { ArrowLeft, ShoppingCart, ClipboardList, Star, Minus, Plus } from 'lucide-react'
import { useState, useMemo } from 'react'
import { ProductDetails, parseDescription, teaserText } from '../components/ProductDetails'

export function ProductDetailView({ product, segment, onBack, onAdd }) {
  const [qty, setQty] = useState(segment === 'retail' ? 1 : product.moq || 1)
  const isRetail = segment === 'retail'
  const blocks = useMemo(() => parseDescription(product.description), [product.description])
  const teaser = useMemo(() => teaserText(blocks), [blocks])

  return (
    <main className="px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <button
          onClick={onBack}
          className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-brand-slate transition-colors hover:text-brand-navy"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to catalog
        </button>

        <div className="grid gap-10 lg:grid-cols-2">
          <div className="overflow-hidden rounded-2xl bg-white shadow-card">
            <img
              src={product.image}
              alt={product.name}
              className="h-full w-full object-cover"
              style={{ maxHeight: '480px' }}
            />
          </div>

          <div>
            <span className="inline-block rounded-full bg-brand-shell px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-ocean">
              {product.category}
            </span>
            <h1 className="mt-3 font-display text-3xl font-bold text-brand-ink sm:text-4xl">{product.name}</h1>

            {isRetail ? (
              <div className="mt-4 flex items-center gap-3">
                <div className="text-3xl font-bold text-brand-navy">€{product.price.toFixed(2)}</div>
                {product.rating && (
                  <div className="flex items-center gap-1 text-sm">
                    <Star className="h-4 w-4 fill-brand-gold text-brand-gold" />
                    <span className="font-semibold text-brand-ink">{product.rating}</span>
                    <span className="text-brand-slate">({Math.floor(Math.random() * 80) + 20} reviews)</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-4 rounded-2xl bg-brand-shell p-5">
                <p className="text-sm font-medium text-brand-slate">
                  Minimum Order Quantity: <span className="font-semibold text-brand-ink">{product.moq} {product.moqUnit}</span>
                </p>
                {product.pricing && (
                  <div className="mt-4 overflow-hidden rounded-lg border border-brand-slate/10 bg-white">
                    <table className="w-full text-sm">
                      <thead className="bg-brand-navy text-white">
                        <tr>
                          <th className="px-4 py-2 text-left">Volume</th>
                          <th className="px-4 py-2 text-right">Unit Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.pricing.map((tier, i) => (
                          <tr key={i} className="border-t border-brand-slate/10">
                            <td className="px-4 py-2.5">
                              ≥ {tier.minQty.toLocaleString()} {product.moqUnit}
                            </td>
                            <td className="px-4 py-2.5 text-right font-semibold text-brand-navy">
                              €{tier.price.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {teaser && <p className="mt-6 leading-relaxed text-brand-slate">{teaser}</p>}

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center rounded-xl border border-brand-slate/20 bg-white p-1">
                <button
                  onClick={() => setQty((q) => Math.max(isRetail ? 1 : product.moq || 1, q - (isRetail ? 1 : product.moq || 1)))}
                  className="rounded-lg p-2.5 text-brand-slate hover:bg-brand-shell"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-16 text-center text-base font-semibold">{qty}</span>
                <button
                  onClick={() => setQty((q) => q + (isRetail ? 1 : product.moq || 1))}
                  className="rounded-lg p-2.5 text-brand-slate hover:bg-brand-shell"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              <button
                onClick={() => onAdd(product, qty)}
                className="flex items-center gap-2 rounded-xl bg-brand-navy px-7 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-ocean"
              >
                {isRetail ? <ShoppingCart className="h-4 w-4" /> : <ClipboardList className="h-4 w-4" />}
                {isRetail ? 'Add to Cart' : 'Add to Inquiry'}
              </button>
            </div>

            {isRetail && product.stock_quantity && (
              <p className="mt-4 text-sm text-brand-slate">
                {product.stock_quantity} units available for immediate dispatch.
              </p>
            )}
          </div>
        </div>

        <ProductDetails description={product.description} />
      </div>
    </main>
  )
}
